import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');

const CATEGORIES = ['IT Equipment','Machines','Power','Vehicles','Furniture & Fixtures','Tools & Equipment'];
const CONDITIONS = ['Excellent','Good','Fair','Poor','Out of Service'];
const MAINTENANCE_TYPES = ['Routine Service','Repair','Calibration','Cleaning','Inspection','Replacement','Upgrade'];

const CAT_ICONS = {
  'IT Equipment':'💻', 'Machines':'⚙', 'Power':'⚡', 
  'Vehicles':'🚗', 'Furniture & Fixtures':'🪑', 'Tools & Equipment':'🔧'
};

const COND_COLORS = {
  'Excellent':{ bg:'#E8F5EB', color:'#2E6B3A' },
  'Good':{ bg:'#EDF4FF', color:'#1E4FAD' },
  'Fair':{ bg:'#FFF3DC', color:'#8B5E0A' },
  'Poor':{ bg:'#FDEAEA', color:'#8F2E24' },
  'Out of Service':{ bg:'#F0F0F0', color:'#8A8A8A' },
};

export default function Assets() {
  const { user } = useAuth();
  const isAdmin = ['super_admin','admin','inventory_officer','front_desk_officer'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';
  const [pendingChanges, setPendingChanges] = useState([]);
  const [showPending, setShowPending] = useState(false);
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [search, setSearch] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(null);
  const [users, setUsers] = useState([]);

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (filterCat) qs.append('category', filterCat);
    if (filterStatus) qs.append('status', filterStatus);
    Promise.all([
      get('/assets?'+qs.toString()),
      get('/assets/stats/summary')
    ]).then(([a, s]) => {
      setAssets(a.data||[]);
      setStats(s);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isSuperAdmin) {
      get('/assets/pending-changes/all').then(d => setPendingChanges(d.data||[])).catch(()=>{});
    }
  }, []);
  useEffect(() => { 
    load(); 
    get('/users').then(d => setUsers(d.data||[]));
  }, [filterCat, filterStatus]);

  const filtered = search
    ? assets.filter(a => a.name.toLowerCase().includes(search.toLowerCase()) ||
        a.serial_number?.toLowerCase().includes(search.toLowerCase()) ||
        a.assigned_to_name?.toLowerCase().includes(search.toLowerCase()))
    : assets;

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this asset from the registry?')) return;
    try { await del('/assets/'+id); load(); }
    catch (err) { alert(err.message); }
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Assets & Equipment</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Track all company devices, machines and equipment</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          {isSuperAdmin && pendingChanges.length > 0 && (
            <button className="btn btn-outline" style={{color:'#8B5E0A',borderColor:'#8B5E0A'}}
              onClick={()=>setShowPending(true)}>
              ⏳ {pendingChanges.length} Pending Edit{pendingChanges.length>1?'s':''}
            </button>
          )}
          {isAdmin && <button className="btn btn-primary" onClick={()=>setShowCreate(true)}>+ Add Asset</button>}
        </div>
      </div>

      {/* Summary tiles */}
      {stats && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10,marginBottom:20}}>
          {[
            { label:'Total Assets', value:stats.summary.total_assets, color:'#3B2A21' },
            { label:'Active', value:stats.summary.active, color:'#556B2F' },
            { label:'Under Repair', value:stats.summary.under_repair, color:'#8B5E0A' },
            { label:'Maintenance Overdue', value:stats.summary.maintenance_overdue, color:'#8F2E24' },
            { label:'Warranty Expiring', value:stats.summary.warranty_expiring, color:'#8B5E0A' },
            { label:'Total Value', value:fmt(stats.summary.total_value), color:'#1E4FAD' },
          ].map(tile => (
            <div key={tile.label} style={{background:'#fff',border:'1px solid var(--border-light)',
              borderRadius:10,padding:'12px 14px',boxShadow:'var(--shadow)'}}>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',
                color:'#8A8A8A',marginBottom:6}}>{tile.label}</div>
              <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',
                fontWeight:700,color:tile.color}}>{tile.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Alerts */}
      {stats && (parseInt(stats.summary.maintenance_overdue)>0 || parseInt(stats.summary.warranty_expiring)>0) && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
          {parseInt(stats.summary.maintenance_overdue)>0 && (
            <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,padding:'10px 16px',fontSize:12,color:'#8F2E24'}}>
              🔧 <strong>{stats.summary.maintenance_overdue}</strong> asset{stats.summary.maintenance_overdue!='1'?'s':''} have overdue maintenance
            </div>
          )}
          {parseInt(stats.summary.warranty_expiring)>0 && (
            <div style={{background:'#FFF3DC',border:'1px solid #F5C842',borderRadius:8,padding:'10px 16px',fontSize:12,color:'#8B5E0A'}}>
              ⚠ <strong>{stats.summary.warranty_expiring}</strong> asset warranty{stats.summary.warranty_expiring!='1'?'s':''} expiring within 30 days
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
        <input className="form-control" style={{maxWidth:200,fontSize:12}} placeholder="Search assets..."
          value={search} onChange={e=>setSearch(e.target.value)} />
        <select className="form-control" style={{width:'auto',fontSize:12}} value={filterCat}
          onChange={e=>setFilterCat(e.target.value)}>
          <option value="">All Categories</option>
          {CATEGORIES.map(c=><option key={c} value={c}>{CAT_ICONS[c]} {c}</option>)}
        </select>
        <select className="form-control" style={{width:'auto',fontSize:12}} value={filterStatus}
          onChange={e=>setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="under_repair">Under Repair</option>
          <option value="disposed">Disposed</option>
        </select>
        {(filterCat||search||filterStatus!=='active') && (
          <button className="btn btn-ghost btn-sm" onClick={()=>{setFilterCat('');setSearch('');setFilterStatus('active');}}>Clear</button>
        )}
      </div>

      {loading ? <div className="spinner"></div> : filtered.length === 0 ? (
        <div className="empty-state">
          <h3>No assets found</h3>
          {isAdmin && <p>Click + Add Asset to register your first asset.</p>}
        </div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:12}}>
          {filtered.map(asset => {
            const cc = COND_COLORS[asset.condition] || COND_COLORS.Good;
            const maintenanceAlert = asset.maintenance_status === 'overdue' || asset.maintenance_status === 'due_soon';
            const warrantyAlert = asset.warranty_status === 'expired' || asset.warranty_status === 'expiring';
            return (
              <div key={asset.id} style={{background:'#fff',border:'2px solid '+(maintenanceAlert||warrantyAlert?'#F5C842':'var(--border-light)'),
                borderRadius:10,padding:16,boxShadow:'var(--shadow)',cursor:'pointer'}}
                onClick={()=>setSelectedAsset(asset.id)}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                  <div style={{fontSize:24}}>{CAT_ICONS[asset.category]||'📦'}</div>
                  <span style={{fontSize:9,fontWeight:700,padding:'3px 10px',borderRadius:20,
                    textTransform:'uppercase',background:cc.bg,color:cc.color}}>
                    {asset.condition}
                  </span>
                </div>
                <div style={{fontSize:14,fontWeight:600,color:'#3B2A21',marginBottom:4}}>{asset.name}</div>
                <div style={{fontSize:11,color:'#8A8A8A',marginBottom:8}}>{asset.category}
                  {asset.brand && ' • '+asset.brand}
                  {asset.model && ' '+asset.model}
                </div>
                {asset.serial_number && (
                  <div style={{fontSize:11,color:'#8A8A8A',marginBottom:4}}>S/N: {asset.serial_number}</div>
                )}
                {asset.assigned_to_name && (
                  <div style={{fontSize:11,color:'#556B2F',marginBottom:4}}>👤 {asset.assigned_to_name}</div>
                )}
                {asset.location && (
                  <div style={{fontSize:11,color:'#8A8A8A',marginBottom:4}}>📍 {asset.location}</div>
                )}
                <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:8}}>
                  {asset.maintenance_status==='overdue' && (
                    <span style={{fontSize:9,background:'#FDEAEA',color:'#8F2E24',padding:'2px 8px',borderRadius:20,fontWeight:700}}>
                      🔧 MAINTENANCE OVERDUE
                    </span>
                  )}
                  {asset.maintenance_status==='due_soon' && (
                    <span style={{fontSize:9,background:'#FFF3DC',color:'#8B5E0A',padding:'2px 8px',borderRadius:20,fontWeight:700}}>
                      🔧 MAINTENANCE DUE SOON
                    </span>
                  )}
                  {asset.warranty_status==='expired' && (
                    <span style={{fontSize:9,background:'#FDEAEA',color:'#8F2E24',padding:'2px 8px',borderRadius:20,fontWeight:700}}>
                      ⚠ WARRANTY EXPIRED
                    </span>
                  )}
                  {asset.warranty_status==='expiring' && (
                    <span style={{fontSize:9,background:'#FFF3DC',color:'#8B5E0A',padding:'2px 8px',borderRadius:20,fontWeight:700}}>
                      ⚠ WARRANTY EXPIRING
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedAsset && (
        <AssetDetailModal assetId={selectedAsset} isAdmin={isAdmin} users={users}
          onClose={()=>setSelectedAsset(null)} onUpdated={()=>{setSelectedAsset(null);load();}} />
      )}

      {showPending && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:640}}>
            <div className="modal-hdr">
              <span className="modal-title">Pending Asset Edit Requests</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowPending(false)}>✕</button>
            </div>
            <div className="modal-body" style={{maxHeight:'70vh',overflowY:'auto'}}>
              {pendingChanges.map(c=>(
                <div key={c.id} style={{background:'#F9F8F5',borderRadius:8,padding:16,marginBottom:12,border:'1px solid #E8E0D4'}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
                    <div style={{fontWeight:600,fontSize:13,color:'#3B2A21'}}>{c.asset_name}</div>
                    <div style={{fontSize:11,color:'#8A8A8A'}}>{new Date(c.requested_at).toLocaleDateString('en-NG')}</div>
                  </div>
                  <div style={{fontSize:12,color:'#8A8A8A',marginBottom:8}}>Requested by: <strong>{c.requested_by_name}</strong></div>
                  <div style={{background:'#fff',borderRadius:6,padding:10,fontSize:12,marginBottom:8}}>
                    <strong>Reason:</strong> {c.reason}
                  </div>
                  <div style={{background:'#fff',borderRadius:6,padding:10,fontSize:11,marginBottom:10,maxHeight:120,overflowY:'auto'}}>
                    <strong>Proposed changes:</strong>
                    <pre style={{margin:'4px 0 0',fontSize:11,color:'#555',whiteSpace:'pre-wrap'}}>
                      {Object.entries(c.proposed_changes).filter(([k,v])=>v).map(([k,v])=>`${k}: ${v}`)}.join('\n')}
                    </pre>
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    <button className="btn btn-primary btn-sm" onClick={async()=>{
                      try {
                        await put('/assets/pending-changes/'+c.id+'/review', {decision:'approved'});
                        setPendingChanges(prev=>prev.filter(x=>x.id!==c.id));
                        alert('Change approved and applied.');
                      } catch(e) { alert(e.message); }
                    }}>✅ Approve</button>
                    <button className="btn btn-outline btn-sm" style={{color:'#8F2E24',borderColor:'#8F2E24'}}
                      onClick={async()=>{
                        const note = prompt('Reason for rejection (optional):');
                        try {
                          await put('/assets/pending-changes/'+c.id+'/review', {decision:'rejected',review_note:note||''});
                          setPendingChanges(prev=>prev.filter(x=>x.id!==c.id));
                          alert('Change rejected.');
                        } catch(e) { alert(e.message); }
                      }}>✗ Reject</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowPending(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {showCreate && (
        <AssetFormModal users={users} onClose={()=>setShowCreate(false)}
          onSaved={()=>{setShowCreate(false);load();}} />
      )}
    </div>
  );
}

function AssetDetailModal({ assetId, isAdmin, users, onClose, onUpdated }) {
  const { user: modalUser } = useAuth();
  const isSuperAdmin = modalUser?.role === 'super_admin';
  const [pendingChanges, setPendingChanges] = useState([]);
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [maintForm, setMaintForm] = useState({
    maintenance_date: new Date().toISOString().split('T')[0],
    maintenance_type:'Routine Service', description:'', cost:'', performed_by:'', condition_after:''
  });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    get('/assets/'+assetId).then(setAsset).finally(()=>setLoading(false));
  };

  useEffect(() => {
    if (isSuperAdmin) {
      get('/assets/pending-changes/all').then(d => setPendingChanges(d.data||[])).catch(()=>{});
    }
  }, []);
  useEffect(() => { load(); }, [assetId]);

  const handleLogMaintenance = async () => {
    setSaving(true);
    try {
      await post('/assets/'+assetId+'/maintenance', maintForm);
      setShowMaintenance(false);
      load();
    } catch (err) { alert(err.message); } finally { setSaving(false); }
  };

  if (loading || !asset) return (
    <div className="modal-overlay"><div className="modal" style={{textAlign:'center',padding:40}}><div className="spinner"></div></div></div>
  );

  const cc = COND_COLORS[asset.condition] || COND_COLORS.Good;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:640}}>
        <div className="modal-hdr">
          <span className="modal-title">{CAT_ICONS[asset.category]} {asset.name}</span>
          <div style={{display:'flex',gap:6}}>
            {isAdmin && <button className="btn btn-outline btn-sm" onClick={()=>setShowEdit(true)}>Edit</button>}
            {isAdmin && <button className="btn btn-outline btn-sm" style={{color:'#556B2F',borderColor:'#556B2F'}}
              onClick={()=>setShowMaintenance(true)}>+ Log Maintenance</button>}
            <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
          </div>
        </div>
        <div className="modal-body">
          {/* Asset details */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16,
            background:'#F2EFE7',borderRadius:8,padding:14}}>
            <div><div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',fontWeight:600,marginBottom:2}}>Category</div>
              <div style={{fontSize:13,fontWeight:500}}>{asset.category}</div></div>
            <div><div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',fontWeight:600,marginBottom:2}}>Condition</div>
              <span style={{fontSize:11,fontWeight:700,padding:'2px 10px',borderRadius:20,background:cc.bg,color:cc.color}}>{asset.condition}</span></div>
            {asset.brand && <div><div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',fontWeight:600,marginBottom:2}}>Brand</div>
              <div style={{fontSize:13}}>{asset.brand}</div></div>}
            {asset.model && <div><div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',fontWeight:600,marginBottom:2}}>Model</div>
              <div style={{fontSize:13}}>{asset.model}</div></div>}
            {asset.serial_number && <div><div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',fontWeight:600,marginBottom:2}}>Serial Number</div>
              <div style={{fontSize:13,fontFamily:'monospace'}}>{asset.serial_number}</div></div>}
            {asset.purchase_date && <div><div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',fontWeight:600,marginBottom:2}}>Purchase Date</div>
              <div style={{fontSize:13}}>{asset.purchase_date}</div></div>}
            {asset.purchase_cost && <div><div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',fontWeight:600,marginBottom:2}}>Purchase Cost</div>
              <div style={{fontSize:13,fontFamily:'Cormorant Garamond,serif',fontWeight:600}}>{fmt(asset.purchase_cost)}</div></div>}
            {asset.supplier && <div><div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',fontWeight:600,marginBottom:2}}>Supplier</div>
              <div style={{fontSize:13}}>{asset.supplier}</div></div>}
            {asset.warranty_expiry && <div><div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',fontWeight:600,marginBottom:2}}>Warranty Expiry</div>
              <div style={{fontSize:13,color:asset.warranty_status==='expired'?'#8F2E24':asset.warranty_status==='expiring'?'#8B5E0A':'inherit',fontWeight:500}}>
                {asset.warranty_expiry} {asset.warranty_status==='expired'?'⚠ EXPIRED':asset.warranty_status==='expiring'?'⚠ EXPIRING SOON':''}
              </div></div>}
            {asset.assigned_to_name && <div><div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',fontWeight:600,marginBottom:2}}>Assigned To</div>
              <div style={{fontSize:13,color:'#556B2F',fontWeight:500}}>👤 {asset.assigned_to_name}</div></div>}
            {asset.location && <div><div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',fontWeight:600,marginBottom:2}}>Location</div>
              <div style={{fontSize:13}}>📍 {asset.location}</div></div>}
            {asset.next_maintenance_date && <div><div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',fontWeight:600,marginBottom:2}}>Next Maintenance</div>
              <div style={{fontSize:13,color:asset.maintenance_status==='overdue'?'#8F2E24':asset.maintenance_status==='due_soon'?'#8B5E0A':'inherit',fontWeight:500}}>
                {asset.next_maintenance_date} {asset.maintenance_status==='overdue'?'🔧 OVERDUE':asset.maintenance_status==='due_soon'?'🔧 DUE SOON':''}
              </div></div>}
          </div>

          {asset.notes && (
            <div style={{background:'#F2EFE7',borderRadius:8,padding:'10px 14px',fontSize:12,marginBottom:16,color:'#555'}}>
              <strong>Notes:</strong> {asset.notes}
            </div>
          )}

          {/* Log maintenance form */}
          {showMaintenance && (
            <div style={{background:'#F2EFE7',borderRadius:8,padding:16,marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:600,color:'#3B2A21',marginBottom:12}}>Log Maintenance</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input type="date" className="form-control" value={maintForm.maintenance_date}
                    onChange={e=>setMaintForm({...maintForm,maintenance_date:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Type *</label>
                  <select className="form-control" value={maintForm.maintenance_type}
                    onChange={e=>setMaintForm({...maintForm,maintenance_type:e.target.value})}>
                    {MAINTENANCE_TYPES.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Performed By</label>
                  <input className="form-control" value={maintForm.performed_by}
                    onChange={e=>setMaintForm({...maintForm,performed_by:e.target.value})}
                    placeholder="Technician or company name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Cost (₦)</label>
                  <input type="number" className="form-control" value={maintForm.cost}
                    onChange={e=>setMaintForm({...maintForm,cost:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Condition After</label>
                  <select className="form-control" value={maintForm.condition_after}
                    onChange={e=>setMaintForm({...maintForm,condition_after:e.target.value})}>
                    <option value="">-- Same as before --</option>
                    {CONDITIONS.map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={2} value={maintForm.description}
                  onChange={e=>setMaintForm({...maintForm,description:e.target.value})}
                  placeholder="What was done..." />
              </div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-outline btn-sm" onClick={()=>setShowMaintenance(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={handleLogMaintenance} disabled={saving}>
                  {saving?'Saving...':'Save Maintenance Record'}
                </button>
              </div>
            </div>
          )}

          {/* Maintenance history */}
          <div>
            <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>
              Maintenance History ({asset.maintenance_history?.length||0} records)
            </div>
            {!asset.maintenance_history?.length ? (
              <div style={{fontSize:12,color:'#8A8A8A',textAlign:'center',padding:16,background:'#F2EFE7',borderRadius:8}}>
                No maintenance records yet
              </div>
            ) : (
              asset.maintenance_history.map((m,i) => (
                <div key={m.id} style={{padding:'10px 14px',background:i%2===0?'#fff':'#F9F9F9',
                  borderRadius:8,marginBottom:6,border:'1px solid var(--border-light)'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:'#3B2A21'}}>{m.maintenance_type}</div>
                      <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>
                        {m.maintenance_date} {m.performed_by&&'• '+m.performed_by}
                      </div>
                      {m.description && <div style={{fontSize:12,color:'#555',marginTop:4}}>{m.description}</div>}
                    </div>
                    <div style={{textAlign:'right'}}>
                      {m.cost && <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:14,fontWeight:600,color:'#8F2E24'}}>{fmt(m.cost)}</div>}
                      {m.condition_after && <div style={{fontSize:10,marginTop:2,color:'#556B2F'}}>{m.condition_after}</div>}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showEdit && (
        <AssetFormModal asset={asset} users={users}
          onClose={()=>setShowEdit(false)}
          onSaved={()=>{setShowEdit(false);load();}} />
      )}
    </div>
  );
}

function AssetFormModal({ asset, users, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = ['super_admin','admin'].includes(user?.role);
  const [reason, setReason] = useState('');
  const [form, setForm] = useState({
    name: asset?.name||'',
    category: asset?.category||'IT Equipment',
    brand: asset?.brand||'',
    model: asset?.model||'',
    serial_number: asset?.serial_number||'',
    purchase_date: asset?.purchase_date||'',
    purchase_cost: asset?.purchase_cost||'',
    supplier: asset?.supplier||'',
    warranty_expiry: asset?.warranty_expiry||'',
    assigned_to: asset?.assigned_to||'',
    location: asset?.location||'',
    condition: asset?.condition||'Good',
    status: asset?.status||'active',
    maintenance_interval_days: asset?.maintenance_interval_days||'',
    notes: asset?.notes||''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!form.name || !form.category) { setError('Name and category required'); return; }
    setSaving(true); setError('');
    try {
      const payload = asset && !isAdmin ? {...form, reason} : form;
      if (asset) {
        const result = await put('/assets/'+asset.id, payload);
        if (result?.pending) {
          alert('Your edit request has been submitted for approval by the Managing Director.');
          onClose();
          return;
        }
      } else {
        await post('/assets', form);
      }
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:560}}>
        <div className="modal-hdr">
          <span className="modal-title">{asset?'Edit':'New'} Asset</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Asset Name *</label>
              <input className="form-control" value={form.name}
                onChange={e=>setForm({...form,name:e.target.value})}
                placeholder="e.g. MacBook Pro, Singer Sewing Machine" />
            </div>
            <div className="form-group">
              <label className="form-label">Category *</label>
              <select className="form-control" value={form.category}
                onChange={e=>setForm({...form,category:e.target.value})}>
                {CATEGORIES.map(c=><option key={c} value={c}>{CAT_ICONS[c]} {c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Brand</label>
              <input className="form-control" value={form.brand}
                onChange={e=>setForm({...form,brand:e.target.value})}
                placeholder="e.g. Apple, Singer, Honda" />
            </div>
            <div className="form-group">
              <label className="form-label">Model</label>
              <input className="form-control" value={form.model}
                onChange={e=>setForm({...form,model:e.target.value})}
                placeholder="e.g. Pro 14, EM200" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Serial Number</label>
              <input className="form-control" value={form.serial_number}
                onChange={e=>setForm({...form,serial_number:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Condition</label>
              <select className="form-control" value={form.condition}
                onChange={e=>setForm({...form,condition:e.target.value})}>
                {CONDITIONS.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Purchase Date</label>
              <input type="date" className="form-control" value={form.purchase_date}
                onChange={e=>setForm({...form,purchase_date:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Purchase Cost (₦)</label>
              <input type="number" className="form-control" value={form.purchase_cost}
                onChange={e=>setForm({...form,purchase_cost:e.target.value})} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Supplier / Vendor</label>
              <input className="form-control" value={form.supplier}
                onChange={e=>setForm({...form,supplier:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Warranty Expiry</label>
              <input type="date" className="form-control" value={form.warranty_expiry}
                onChange={e=>setForm({...form,warranty_expiry:e.target.value})} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Assigned To</label>
              <select className="form-control" value={form.assigned_to}
                onChange={e=>setForm({...form,assigned_to:e.target.value})}>
                <option value="">-- Not assigned --</option>
                {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Location</label>
              <input className="form-control" value={form.location}
                onChange={e=>setForm({...form,location:e.target.value})}
                placeholder="e.g. Factory Floor, Office, Store" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={form.status}
                onChange={e=>setForm({...form,status:e.target.value})}>
                <option value="active">Active</option>
                <option value="under_repair">Under Repair</option>
                <option value="disposed">Disposed</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Maintenance Interval (days)</label>
              <input type="number" className="form-control" value={form.maintenance_interval_days}
                onChange={e=>setForm({...form,maintenance_interval_days:e.target.value})}
                placeholder="e.g. 90 for quarterly" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={form.notes}
              onChange={e=>setForm({...form,notes:e.target.value})}
              placeholder="Any additional notes..." />
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving?'Saving...':asset?'Save Changes':'Add Asset'}
          </button>
        </div>
      </div>
    </div>
  );
}
